import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import { Badge, Button, ErrorState, Skeleton, Separator } from '@vta/ui';
import { RequireAdmin } from '../components/RequireAdmin.tsx';
import { apiClient, ApiFetchError } from '../lib/api-client.ts';
import type { AdminSubmission } from '../features/admin/SubmissionReviewTable.tsx';
import type { SyncRunRow } from '../features/admin/SyncStatusPanel.tsx';

/**
 * `/admin` landing — quick counts and shortcuts. Backend gates each call with
 * `requireRole('admin')`; the frontend wraps in `RequireAdmin` for UX so a
 * non-admin sees a friendly empty state instead of an opaque 401/403.
 */
export const Route = createFileRoute('/admin/')({
  component: AdminIndexPage,
});

function AdminIndexPage() {
  return (
    <RequireAdmin>
      <AdminDashboard />
    </RequireAdmin>
  );
}

interface SubmissionsResponse {
  submissions: AdminSubmission[];
}

interface SyncResponse {
  runs: SyncRunRow[];
}

function AdminDashboard() {
  const [pendingCount, setPendingCount] = React.useState<number | null>(null);
  const [recentRuns, setRecentRuns] = React.useState<SyncRunRow[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      apiClient.get<SubmissionsResponse>('/api/admin/submissions', {
        query: { status: 'pending', limit: 1 },
      }),
      apiClient.get<SyncResponse>('/api/admin/sync/status', { query: { limit: 5 } }),
    ])
      .then(([subs, sync]) => {
        if (!active) return;
        setPendingCount(subs.submissions.length);
        setRecentRuns(sync.runs);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setError(err instanceof ApiFetchError ? err.message : 'Failed to load admin overview');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Admin · VEX Teams Atlas</title>
      </Helmet>
      <div className="mx-auto w-full max-w-4xl px-4 py-10 md:py-14">
        <header className="mb-8 flex flex-col gap-2">
          <p className="text-2xs uppercase tracking-[0.32em] text-fg-subtle">Admin</p>
          <h1 className="font-display text-3xl tracking-tight text-fg md:text-4xl">
            Moderation dashboard
          </h1>
        </header>

        {error ? <ErrorState title="Couldn't load dashboard" description={error} /> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <DashboardCard
            label="Pending submissions"
            value={loading ? null : pendingCount}
            cta={
              <Button asChild variant="accent" size="sm">
                <Link to="/admin/submissions">Review queue</Link>
              </Button>
            }
          />
          <DashboardCard
            label="Recent sync runs"
            value={loading ? null : (recentRuns?.length ?? 0)}
            cta={
              <Button asChild variant="outline" size="sm">
                <Link to="/admin/sync">Sync panel</Link>
              </Button>
            }
          />
        </div>

        <Separator className="my-8" />

        <section className="flex flex-col gap-3">
          <h2 className="text-xs font-medium uppercase tracking-[0.18em] text-fg-subtle">
            Latest sync activity
          </h2>
          {loading ? (
            <Skeleton className="h-24 w-full" />
          ) : recentRuns && recentRuns.length > 0 ? (
            <ul className="flex flex-col divide-y divide-border-subtle rounded-md border border-border-subtle">
              {recentRuns.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant={r.ok ? 'success' : r.finishedAt ? 'danger' : 'accent'}>
                      {r.ok ? 'ok' : r.finishedAt ? 'failed' : 'running'}
                    </Badge>
                    <span className="font-mono text-2xs uppercase tracking-[0.14em] text-fg">
                      {r.job}
                    </span>
                  </div>
                  <span className="text-2xs uppercase tracking-[0.14em] text-fg-subtle">
                    {new Date(r.startedAt).toLocaleString()} · {r.processed} processed,{' '}
                    {r.errors} errors
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="rounded-md border border-dashed border-border-subtle px-4 py-8 text-center text-sm text-fg-muted">
              No sync runs recorded yet.
            </p>
          )}
        </section>
      </div>
    </>
  );
}

function DashboardCard({
  label,
  value,
  cta,
}: {
  label: string;
  value: number | null;
  cta: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-md border border-border-subtle bg-bg-raised p-5">
      <p className="text-2xs uppercase tracking-[0.18em] text-fg-subtle">{label}</p>
      <p className="font-display text-3xl text-fg">
        {value === null ? <Skeleton className="h-9 w-16" /> : value}
      </p>
      <div className="mt-auto">{cta}</div>
    </div>
  );
}
